import { action, computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../../commonv3/api/RemoteResourceV3';
import { SessionShowView, SessionType } from '../../../../types/sessions';
import { SessionId } from '../../../../../commonv3/types/sessions';
import { SessionApis } from '../../../../apis/sessions';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { EnhancedLocationDescriptor, V3Routes } from '../../../../routes';
import { translateErrorMessage } from '../../../../../commonv3/api/protocol';
import { ReservationApis } from '../../../../apis/reservations';
import { ReservableType } from '../../../../../commonv3/types/reservable';
import { HomePageState } from '../../../../containers/home/states/HomePageState';
import { CoachingPageState } from '../../../../containers/coaching/states/CoachingPageState';
import { MokumokuPageState } from '../../../../containers/mokumoku/states/MokumokuPageState';

const MAX_MOKUMOKU_RESERVABLE_COUNT = 3;

export class SessionReservationPaneState {
  homeState: HomePageState | CoachingPageState | MokumokuPageState | null = null;
  sessionId: SessionId | null = null;
  session: RemoteResourceV3<SessionShowView> = RemoteResourceV3.loading();
  submitting = false;

  constructor(homeState: HomePageState | CoachingPageState | MokumokuPageState) {
    makeObservable(this, {
      sessionId: observable,
      session: observable,
      submitting: observable,
      init: action.bound,
      exceedsMokumokuLimit: computed,
      reloadSession: action.bound,
      submit: action.bound,
    });

    this.homeState = homeState;
  }

  async init(sessionId: SessionId): Promise<void> {
    this.sessionId = sessionId;
    const session = await this.reloadSession();
    await GlobalStatesV3.currentUser.reloadUser();
    if (!GlobalStatesV3.currentUser.availableTokens.isSessionAffordable(session)) {
      GlobalStatesV3.routing.push(
        V3Routes.sessionReservationError(this.sessionId, 'すでに今月の予約数の上限に達しています')
      );
    }
  }

  get exceedsMokumokuLimit() {
    const { reservationList } = this.homeState;
    const mokumokuReservations = reservationList.findSessionReservationsByType(SessionType.MOKUMOKU);
    return mokumokuReservations.length >= MAX_MOKUMOKU_RESERVABLE_COUNT;
  }

  async reloadSession() {
    return await updateRemoteResourceV3(
      this.session,
      (v) => (this.session = v),
      () => SessionApis.show(this.sessionId)
    );
  }

  async submit() {
    const getSessionReservationCompletePath = (): EnhancedLocationDescriptor | null => {
      switch (true) {
        case location.pathname.includes('coaching'):
          return V3Routes.coachingSessionReservationComplete(this.sessionId);
        case location.pathname.includes('mokumoku'):
          return V3Routes.mokumokuSessionReservationComplete(this.sessionId);
        default:
          return null;
      }
    };
    const sessionReservationCompletePath = getSessionReservationCompletePath();

    try {
      this.submitting = true;
      await ReservationApis.create(ReservableType.SESSION, this.sessionId);
      await Promise.all([await GlobalStatesV3.currentUser.reloadUser(), await this.homeState.reservationList.init()]);
      GlobalStatesV3.routing.push(sessionReservationCompletePath);
    } catch (e) {
      GlobalStatesV3.routing.push(V3Routes.sessionReservationError(this.sessionId, translateErrorMessage(e)));
    } finally {
      this.submitting = false;
    }
  }
}
